import type { BusinessAnalyticsRow } from '../api/useBusinessAnalytics';

export interface TopEntry {
  business_id: string;
  business_name: string;
  value: number;
}

export interface AggregateStats {
  business_count: number;

  // Counts summed across all businesses.
  totals: {
    check_ins_total: number;
    going_intents_submitted: number;
    live_check_ins_started: number;
    live_check_ins_completed: number;
    live_check_ins_active_now: number;
    favorites_total: number;
    events_created: number;
    events_completed: number;
    events_cancelled: number;
    events_recurring: number;
    event_interests_going: number;
    event_interests_interested: number;
    event_interests_removed: number;
    event_analytics_rows: number;
    event_planned_attendees_total: number;
    event_actual_attendees_total: number;
    deals_created: number;
    deals_active_now: number;
    deals_scheduled: number;
    deals_expired: number;
    deals_deleted: number;
    deal_views_total: number;
    deal_views_from_business_page: number;
    deal_views_from_notification: number;
    deal_notifications_sent: number;
    deal_notifications_tapped: number;
    deal_notifications_failed: number;
    business_notifications_created: number;
    business_notifications_sent_rows: number;
    business_notification_deliveries_sent: number;
    business_notification_deliveries_delivered: number;
    business_notification_deliveries_opened: number;
    weekly_notifications_sent: number;
    weekly_notifications_opened: number;
    weekly_attributed_visits: number;
    cover_history_entries: number;
    cover_history_set: number;
    cover_history_updated: number;
    cover_history_cleared: number;
    cover_history_expired: number;
    cover_notifications_sent: number;
    cover_notifications_tapped: number;
    cover_notifications_failed: number;
    admin_audit_events: number;
  };

  // Sums of per-business distinct-user counts. Same user across multiple
  // venues counts once per venue — surface with a caveat in the UI.
  distinctSums: {
    check_ins_distinct_users: number;
    live_check_in_distinct_users: number;
    deal_views_distinct_users: number;
  };

  // Weighted averages: per-business means reweighted by the denominator that
  // produced them. Null when the weight sum is zero (divide-by-zero guard).
  weightedAverages: {
    live_check_in_avg_minutes: number | null;
  };

  // Derived ratios computed from aggregate numerators/denominators. Always
  // safer than averaging per-business rates.
  rates: {
    notification_delivery_rate: number | null; // delivered / sent
    notification_open_rate: number | null; // opened / delivered
    deal_tap_through_rate: number | null; // tapped / sent
    cover_notification_open_rate: number | null; // tapped / sent
    live_completion_rate: number | null; // completed / started
    event_conversion_pct: number | null; // actual / planned × 100
  };

  perBusinessAverages: {
    check_ins_per_business: number;
    favorites_per_business: number;
    live_sessions_per_business: number;
    notifications_sent_per_business: number;
  };

  maxes: {
    live_check_in_max_minutes: number;
  };

  latest: {
    last_check_in_at: string | null;
    last_live_check_in_at: string | null;
    latest_summary_week: string | null;
    latest_crowd_week: string | null;
  };

  coverActiveCount: number;

  topPerformers: {
    check_ins: TopEntry[];
    favorites: TopEntry[];
    notif_opens: TopEntry[];
    live_sessions: TopEntry[];
    deal_views: TopEntry[];
  };
}

function sumField(rows: BusinessAnalyticsRow[], key: keyof BusinessAnalyticsRow): number {
  let acc = 0;
  for (const row of rows) {
    const v = row[key];
    if (typeof v === 'number' && Number.isFinite(v)) acc += v;
  }
  return acc;
}

function maxDate(rows: BusinessAnalyticsRow[], key: keyof BusinessAnalyticsRow): string | null {
  let best: number | null = null;
  let bestStr: string | null = null;
  for (const row of rows) {
    const v = row[key];
    if (typeof v !== 'string' || !v) continue;
    const t = new Date(v).getTime();
    if (Number.isNaN(t)) continue;
    if (best === null || t > best) {
      best = t;
      bestStr = v;
    }
  }
  return bestStr;
}

function maxField(rows: BusinessAnalyticsRow[], key: keyof BusinessAnalyticsRow): number {
  let best = 0;
  for (const row of rows) {
    const v = row[key];
    if (typeof v === 'number' && Number.isFinite(v) && v > best) best = v;
  }
  return best;
}

function safeRatio(num: number, den: number): number | null {
  if (!Number.isFinite(den) || den === 0) return null;
  return num / den;
}

function topN(
  rows: BusinessAnalyticsRow[],
  key: keyof BusinessAnalyticsRow,
  limit = 10,
): TopEntry[] {
  const entries: TopEntry[] = [];
  for (const row of rows) {
    const v = row[key];
    if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) continue;
    entries.push({
      business_id: row.business_id,
      business_name: row.business_name,
      value: v,
    });
  }
  entries.sort((a, b) => b.value - a.value);
  return entries.slice(0, limit);
}

export function aggregateRows(rows: BusinessAnalyticsRow[]): AggregateStats {
  const business_count = rows.length;

  // Sums ───────────────────────────────────────────────────────────────────
  const totals = {
    check_ins_total: sumField(rows, 'check_ins_total'),
    going_intents_submitted: sumField(rows, 'going_intents_submitted'),
    live_check_ins_started: sumField(rows, 'live_check_ins_started'),
    live_check_ins_completed: sumField(rows, 'live_check_ins_completed'),
    live_check_ins_active_now: sumField(rows, 'live_check_ins_active_now'),
    favorites_total: sumField(rows, 'favorites_total'),
    events_created: sumField(rows, 'events_created'),
    events_completed: sumField(rows, 'events_completed'),
    events_cancelled: sumField(rows, 'events_cancelled'),
    events_recurring: sumField(rows, 'events_recurring'),
    event_interests_going: sumField(rows, 'event_interests_going'),
    event_interests_interested: sumField(rows, 'event_interests_interested'),
    event_interests_removed: sumField(rows, 'event_interests_removed'),
    event_analytics_rows: sumField(rows, 'event_analytics_rows'),
    event_planned_attendees_total: sumField(rows, 'event_planned_attendees_total'),
    event_actual_attendees_total: sumField(rows, 'event_actual_attendees_total'),
    deals_created: sumField(rows, 'deals_created'),
    deals_active_now: sumField(rows, 'deals_active_now'),
    deals_scheduled: sumField(rows, 'deals_scheduled'),
    deals_expired: sumField(rows, 'deals_expired'),
    deals_deleted: sumField(rows, 'deals_deleted'),
    deal_views_total: sumField(rows, 'deal_views_total'),
    deal_views_from_business_page: sumField(rows, 'deal_views_from_business_page'),
    deal_views_from_notification: sumField(rows, 'deal_views_from_notification'),
    deal_notifications_sent: sumField(rows, 'deal_notifications_sent'),
    deal_notifications_tapped: sumField(rows, 'deal_notifications_tapped'),
    deal_notifications_failed: sumField(rows, 'deal_notifications_failed'),
    business_notifications_created: sumField(rows, 'business_notifications_created'),
    business_notifications_sent_rows: sumField(rows, 'business_notifications_sent_rows'),
    business_notification_deliveries_sent: sumField(rows, 'business_notification_deliveries_sent'),
    business_notification_deliveries_delivered: sumField(rows, 'business_notification_deliveries_delivered'),
    business_notification_deliveries_opened: sumField(rows, 'business_notification_deliveries_opened'),
    weekly_notifications_sent: sumField(rows, 'weekly_notifications_sent'),
    weekly_notifications_opened: sumField(rows, 'weekly_notifications_opened'),
    weekly_attributed_visits: sumField(rows, 'weekly_attributed_visits'),
    cover_history_entries: sumField(rows, 'cover_history_entries'),
    cover_history_set: sumField(rows, 'cover_history_set'),
    cover_history_updated: sumField(rows, 'cover_history_updated'),
    cover_history_cleared: sumField(rows, 'cover_history_cleared'),
    cover_history_expired: sumField(rows, 'cover_history_expired'),
    cover_notifications_sent: sumField(rows, 'cover_notifications_sent'),
    cover_notifications_tapped: sumField(rows, 'cover_notifications_tapped'),
    cover_notifications_failed: sumField(rows, 'cover_notifications_failed'),
    admin_audit_events: sumField(rows, 'admin_audit_events'),
  };

  // Distinct-user sums (labeled as per-venue sums in the UI) ─────────────
  const distinctSums = {
    check_ins_distinct_users: sumField(rows, 'check_ins_distinct_users'),
    live_check_in_distinct_users: sumField(rows, 'live_check_in_distinct_users'),
    deal_views_distinct_users: sumField(rows, 'deal_views_distinct_users'),
  };

  // Weighted average: live session avg minutes weighted by completed sessions.
  let minutesWeighted = 0;
  let minutesWeightTotal = 0;
  for (const row of rows) {
    const avg = row.live_check_in_avg_minutes;
    const weight = row.live_check_ins_completed;
    if (typeof avg === 'number' && Number.isFinite(avg)
        && typeof weight === 'number' && weight > 0) {
      minutesWeighted += avg * weight;
      minutesWeightTotal += weight;
    }
  }
  const weightedAverages = {
    live_check_in_avg_minutes: safeRatio(minutesWeighted, minutesWeightTotal),
  };

  // Derived ratios from aggregate numerators/denominators.
  const rates = {
    notification_delivery_rate: safeRatio(
      totals.business_notification_deliveries_delivered,
      totals.business_notification_deliveries_sent,
    ),
    notification_open_rate: safeRatio(
      totals.business_notification_deliveries_opened,
      totals.business_notification_deliveries_delivered,
    ),
    deal_tap_through_rate: safeRatio(
      totals.deal_notifications_tapped,
      totals.deal_notifications_sent,
    ),
    cover_notification_open_rate: safeRatio(
      totals.cover_notifications_tapped,
      totals.cover_notifications_sent,
    ),
    live_completion_rate: safeRatio(
      totals.live_check_ins_completed,
      totals.live_check_ins_started,
    ),
    event_conversion_pct: (() => {
      const r = safeRatio(
        totals.event_actual_attendees_total,
        totals.event_planned_attendees_total,
      );
      return r === null ? null : r * 100;
    })(),
  };

  const perBusinessAverages = business_count > 0
    ? {
        check_ins_per_business: totals.check_ins_total / business_count,
        favorites_per_business: totals.favorites_total / business_count,
        live_sessions_per_business: totals.live_check_ins_started / business_count,
        notifications_sent_per_business:
          totals.business_notifications_created / business_count,
      }
    : {
        check_ins_per_business: 0,
        favorites_per_business: 0,
        live_sessions_per_business: 0,
        notifications_sent_per_business: 0,
      };

  const maxes = {
    live_check_in_max_minutes: maxField(rows, 'live_check_in_max_minutes'),
  };

  const latest = {
    last_check_in_at: maxDate(rows, 'last_check_in_at'),
    last_live_check_in_at: maxDate(rows, 'last_live_check_in_at'),
    latest_summary_week: maxDate(rows, 'latest_summary_week'),
    latest_crowd_week: maxDate(rows, 'latest_crowd_week'),
  };

  let coverActiveCount = 0;
  for (const row of rows) {
    if (row.cover_currently_active === true) coverActiveCount += 1;
  }

  const topPerformers = {
    check_ins: topN(rows, 'check_ins_total'),
    favorites: topN(rows, 'favorites_total'),
    notif_opens: topN(rows, 'business_notification_deliveries_opened'),
    live_sessions: topN(rows, 'live_check_ins_started'),
    deal_views: topN(rows, 'deal_views_total'),
  };

  return {
    business_count,
    totals,
    distinctSums,
    weightedAverages,
    rates,
    perBusinessAverages,
    maxes,
    latest,
    coverActiveCount,
    topPerformers,
  };
}
